import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getStats, getSyncEvents } from '../api';
import { formatRelativeTime, getEventTypeLabel, getEventTypeIcon } from '../utils';
import type { SyncEventStats, SyncEvent, EventType } from '../types';
import styles from './Dashboard.module.css';

/**
 * Dashboard Page
 * 
 * Overview of sync event statistics and recent activity.
 * Computes stats from events when the stats endpoint fails.
 */

// Helper to normalize events_by_type from API (handles both array and object formats)
function normalizeEventsByType(data: unknown): Record<string, number> {
  if (!data) return {};
  
  if (typeof data === 'object' && !Array.isArray(data)) {
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (typeof value === 'number') {
        result[key] = value;
      } else if (typeof value === 'object' && value !== null && 'count' in value) {
        result[key] = (value as { count: number }).count;
      }
    }
    return result;
  }
  
  if (Array.isArray(data)) {
    const result: Record<string, number> = {};
    for (const item of data) {
      if (typeof item === 'object' && item !== null) {
        const type = (item as Record<string, unknown>).type || (item as Record<string, unknown>).event_type;
        const count = (item as Record<string, unknown>).count || (item as Record<string, unknown>).total || 0;
        if (typeof type === 'string' && typeof count === 'number') {
          result[type] = count;
        }
      }
    }
    return result;
  }
  
  return {};
}

// Corruption stats by event type
interface CorruptionByType {
  uploads: { total: number; corrupted: number };
  downloads: { total: number; corrupted: number };
  handshakes: { total: number; corrupted: number };
}

// Compute stats from events when stats endpoint fails
function computeStatsFromEvents(events: SyncEvent[]): Partial<SyncEventStats> & { corruptionByType?: CorruptionByType } {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  
  const eventsByType: Record<string, number> = {};
  const users = new Set<string>();
  const devices = new Set<string>();
  let corruptionCount = 0;
  let quarantinedCount = 0;
  let eventsLast24h = 0;
  let corruptionLast24h = 0;
  let quarantinedLast24h = 0;
  let massDeduplicationCount = 0;
  let conflictsCreated = 0;
  let conflictsResolved = 0;
  
  // Track corruption by event type
  const corruptionByType: CorruptionByType = {
    uploads: { total: 0, corrupted: 0 },
    downloads: { total: 0, corrupted: 0 },
    handshakes: { total: 0, corrupted: 0 },
  };
  
  for (const event of events) {
    // Event type counting
    const type = event.event_type;
    eventsByType[type] = (eventsByType[type] || 0) + 1;
    
    // Track corruption by event type
    if (type === 'file_upload') {
      corruptionByType.uploads.total++;
      if (event.has_suspicious_chars) corruptionByType.uploads.corrupted++;
    } else if (type === 'file_download') {
      corruptionByType.downloads.total++;
      if (event.has_suspicious_chars) corruptionByType.downloads.corrupted++;
    } else if (type === 'sync_handshake') {
      corruptionByType.handshakes.total++;
      if (event.has_suspicious_chars) corruptionByType.handshakes.corrupted++;
    }
    
    // Unique users/devices
    if (event.user_email) users.add(event.user_email);
    if (event.device_code) devices.add(event.device_code);
    
    // Corruption
    if (event.has_suspicious_chars) corruptionCount++;
    if (event.was_quarantined) quarantinedCount++;
    
    // Deduplication
    if (event.is_mass_deduplication) massDeduplicationCount++;
    
    // Conflicts
    if (event.event_type === 'conflict_created') conflictsCreated++;
    if (event.event_type === 'conflict_resolved') conflictsResolved++;
    
    // 24h stats
    const eventDate = new Date(event.timestamp);
    if (eventDate >= yesterday) {
      eventsLast24h++;
      if (event.has_suspicious_chars) corruptionLast24h++;
      if (event.was_quarantined) quarantinedLast24h++;
    }
  }
  
  return {
    total_events: events.length,
    events_by_type: eventsByType as Record<EventType, number>,
    corruption_count: corruptionCount,
    corruption_rate: events.length > 0 ? corruptionCount / events.length : 0,
    quarantined_count: quarantinedCount,
    quarantined_last_24h: quarantinedLast24h,
    error_count: events.filter(e => !e.success).length,
    unique_users: users.size,
    unique_devices: devices.size,
    events_last_24h: eventsLast24h,
    corruption_last_24h: corruptionLast24h,
    mass_deduplication_count: massDeduplicationCount,
    conflicts_created_count: conflictsCreated,
    conflicts_resolved_count: conflictsResolved,
    conflicts_pending_count: Math.max(0, conflictsCreated - conflictsResolved),
    corruptionByType,
  };
}

export default function Dashboard() {
  const [stats, setStats] = useState<Partial<SyncEventStats> | null>(null);
  const [allEvents, setAllEvents] = useState<SyncEvent[]>([]);
  const [recentEvents, setRecentEvents] = useState<SyncEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statsSource, setStatsSource] = useState<'api' | 'computed'>('api');

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      setError(null);
      
      try {
        // Get timestamp for 24 hours ago
        const yesterday = new Date();
        yesterday.setHours(yesterday.getHours() - 24);
        const timestamp24hAgo = yesterday.toISOString();
        
        // Fetch stats from API and events from last 24 hours
        const [statsData, events24hFirstPage] = await Promise.all([
          getStats().catch((e) => { 
            console.warn('[Dashboard] Stats endpoint failed, will compute from events:', e); 
            return null; 
          }),
          getSyncEvents({ 
            page_size: 200, 
            ordering: '-timestamp',
            timestamp_gte: timestamp24hAgo,
          }).catch(() => ({ results: [], count: 0 })),
        ]);
        
        // Fetch all pages of 24h events (up to 2000 for dashboard)
        let all24hEvents = [...(events24hFirstPage.results || [])];
        const total24h = events24hFirstPage.count || 0;
        const maxPages = Math.min(Math.ceil(total24h / 200), 10); // Max 10 pages = 2000 events
        
        if (maxPages > 1) {
          const additionalPages = await Promise.all(
            Array.from({ length: maxPages - 1 }, (_, i) => 
              getSyncEvents({
                page_size: 200,
                ordering: '-timestamp',
                timestamp_gte: timestamp24hAgo,
                page: i + 2,
              }).catch(() => ({ results: [] }))
            )
          );
          for (const page of additionalPages) {
            all24hEvents = [...all24hEvents, ...(page.results || [])];
          }
        }
        
        console.log(`[Dashboard] Fetched ${all24hEvents.length} events from last 24 hours (total: ${total24h})`);
        
        setAllEvents(all24hEvents);
        setRecentEvents(all24hEvents.slice(0, 15));
        
        if (statsData && statsData.total_events !== undefined) {
          // Use API stats but override with computed 24h stats for accuracy
          const computed24h = computeStatsFromEvents(all24hEvents);
          console.log('[Dashboard] Stats from API with 24h computed overlay:', statsData);
          setStats({
            ...statsData,
            // Override with accurate 24h computed values
            events_last_24h: all24hEvents.length,
            corruption_last_24h: computed24h.corruption_last_24h,
            quarantined_last_24h: computed24h.quarantined_last_24h,
            // Use computed corruption rates from 24h data
            mass_deduplication_count: computed24h.mass_deduplication_count,
            conflicts_created_count: computed24h.conflicts_created_count,
            conflicts_resolved_count: computed24h.conflicts_resolved_count,
            conflicts_pending_count: computed24h.conflicts_pending_count,
            // Cast to include our extended type
            ...({ corruptionByType: computed24h.corruptionByType } as Record<string, unknown>),
          } as Partial<SyncEventStats>);
          setStatsSource('api');
        } else if (all24hEvents.length > 0) {
          // Compute all stats from 24h events
          const computed = computeStatsFromEvents(all24hEvents);
          console.log('[Dashboard] Computed stats from 24h events:', computed);
          setStats(computed);
          setStatsSource('computed');
        } else {
          setStats(null);
        }
      } catch (err) {
        console.error('[Dashboard] Error fetching data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  // Compute event type distribution
  const eventsByType = useMemo(() => {
    if (stats?.events_by_type) {
      return normalizeEventsByType(stats.events_by_type);
    }
    return {};
  }, [stats]);

  // Get corruption by type stats
  const corruptionByType = useMemo(() => {
    const cbt = (stats as ReturnType<typeof computeStatsFromEvents>)?.corruptionByType;
    if (!cbt) {
      return {
        uploads: { total: 0, corrupted: 0, rate: 0 },
        downloads: { total: 0, corrupted: 0, rate: 0 },
        handshakes: { total: 0, corrupted: 0, rate: 0 },
      };
    }
    return {
      uploads: { 
        ...cbt.uploads, 
        rate: cbt.uploads.total > 0 ? (cbt.uploads.corrupted / cbt.uploads.total) * 100 : 0 
      },
      downloads: { 
        ...cbt.downloads, 
        rate: cbt.downloads.total > 0 ? (cbt.downloads.corrupted / cbt.downloads.total) * 100 : 0 
      },
      handshakes: { 
        ...cbt.handshakes, 
        rate: cbt.handshakes.total > 0 ? (cbt.handshakes.corrupted / cbt.handshakes.total) * 100 : 0 
      },
    };
  }, [stats]);

  if (isLoading) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.loading}>Loading dashboard...</div>
      </div>
    );
  }

  const hasStats = stats && (stats.total_events ?? 0) > 0;

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1 className={styles.title}>Dashboard</h1>
        <p className={styles.subtitle}>
          Last 24 hours: {allEvents.length.toLocaleString()} events
          {statsSource === 'computed' && <span className={styles.computedBadge}> (computed)</span>}
        </p>
      </div>

      {error && (
        <div className={styles.error}>{error}</div>
      )}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {allEvents.length.toLocaleString()}
          </div>
          <div className={styles.statLabel}>Events (24h)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {hasStats ? (stats.unique_users?.toLocaleString() ?? '0') : '0'}
          </div>
          <div className={styles.statLabel}>Active Users (24h)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {hasStats ? (stats.unique_devices?.toLocaleString() ?? '0') : '0'}
          </div>
          <div className={styles.statLabel}>Active Devices (24h)</div>
        </div>
        <div className={`${styles.statCard} ${corruptionByType.uploads.corrupted > 0 ? styles.alertCard : ''}`}>
          <div className={styles.statValue}>
            {corruptionByType.uploads.rate.toFixed(1)}%
          </div>
          <div className={styles.statLabel}>📤 Upload Corrupt</div>
          {corruptionByType.uploads.total > 0 && (
            <div className={styles.statSubtext}>
              {corruptionByType.uploads.corrupted} of {corruptionByType.uploads.total.toLocaleString()}
            </div>
          )}
        </div>
        <div className={`${styles.statCard} ${corruptionByType.downloads.corrupted > 0 ? styles.alertCard : ''}`}>
          <div className={styles.statValue}>
            {corruptionByType.downloads.rate.toFixed(1)}%
          </div>
          <div className={styles.statLabel}>📥 Download Corrupt</div>
          {corruptionByType.downloads.total > 0 && (
            <div className={styles.statSubtext}>
              {corruptionByType.downloads.corrupted} of {corruptionByType.downloads.total.toLocaleString()}
            </div>
          )}
        </div>
        <div className={`${styles.statCard} ${(stats?.quarantined_count ?? 0) > 0 ? styles.quarantineCard : ''}`}>
          <div className={styles.statValue}>
            {hasStats ? (stats.quarantined_count?.toLocaleString() ?? '0') : '0'}
          </div>
          <div className={styles.statLabel}>🔒 Quarantined</div>
          {(stats?.quarantined_last_24h ?? 0) > 0 && (
            <div className={styles.statSubtext}>
              {stats?.quarantined_last_24h} in last 24h
            </div>
          )}
        </div>
        <div className={`${styles.statCard} ${(stats?.mass_deduplication_count ?? 0) > 0 ? styles.deduplicationCard : ''}`}>
          <div className={styles.statValue}>
            {hasStats ? (stats.mass_deduplication_count?.toLocaleString() ?? '0') : '0'}
          </div>
          <div className={styles.statLabel}>🔀 Mass Dedup</div>
        </div>
        <div className={`${styles.statCard} ${(stats?.conflicts_pending_count ?? 0) > 0 ? styles.conflictCard : ''}`}>
          <div className={styles.statValue}>
            {hasStats ? (stats.conflicts_pending_count?.toLocaleString() ?? '0') : '0'}
          </div>
          <div className={styles.statLabel}>⚡ Pending Conflicts</div>
          {(stats?.conflicts_created_count ?? 0) > 0 && (
            <div className={styles.statSubtext}>
              {stats?.conflicts_resolved_count ?? 0} of {stats?.conflicts_created_count ?? 0} resolved
            </div>
          )}
        </div>
      </div>

      {Object.keys(eventsByType).length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Events by Type</h2>
          <div className={styles.typeGrid}>
            {Object.entries(eventsByType)
              .sort(([, a], [, b]) => b - a)
              .map(([type, count]) => (
                <div key={type} className={styles.typeCard}>
                  <span className={styles.typeIcon}>{getEventTypeIcon(type as EventType)}</span>
                  <span className={styles.typeName}>{getEventTypeLabel(type as EventType)}</span>
                  <span className={styles.typeCount}>{count.toLocaleString()}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Recent Activity</h2>
          <Link to="/events" className={styles.viewAllLink}>View all →</Link>
        </div>
        
        {recentEvents.length === 0 ? (
          <div className={styles.placeholder}>
            <p>No sync events found.</p>
          </div>
        ) : (
          <div className={styles.eventList}>
            {recentEvents.map((event) => (
              <Link 
                key={event.id} 
                to={`/events/${event.id}`}
                className={styles.eventItem}
              >
                <span className={styles.eventIcon}>
                  {getEventTypeIcon(event.event_type)}
                </span>
                <div className={styles.eventDetails}>
                  <div className={styles.eventMain}>
                    <span className={styles.eventType}>
                      {getEventTypeLabel(event.event_type)}
                    </span>
                    {event.file_name && (
                      <span className={styles.eventFile}>{event.file_name}</span>
                    )}
                  </div>
                  <div className={styles.eventMeta}>
                    <span>{event.user_email || 'Unknown user'}</span>
                    <span className={styles.separator}>•</span>
                    <span>{event.device_code || 'Unknown device'}</span>
                    <span className={styles.separator}>•</span>
                    <span>{formatRelativeTime(event.timestamp)}</span>
                  </div>
                </div>
                <div className={styles.eventBadges}>
                  {event.has_suspicious_chars && (
                    <span className={styles.corruptedBadge}>⚠️ Corrupted</span>
                  )}
                  {event.was_quarantined && (
                    <span className={
                      event.quarantine_reason === 'SEVERE_TRUNCATION' ? styles.truncationBadge : 
                      event.quarantine_reason === 'EMPTY_UPLOAD' ? styles.emptyUploadBadge :
                      styles.quarantineBadge
                    }>
                      {event.quarantine_reason === 'SEVERE_TRUNCATION' ? '✂️ Truncated' : 
                       event.quarantine_reason === 'EMPTY_UPLOAD' ? '📭 Empty' :
                       '🔒 Quarantined'}
                    </span>
                  )}
                  {event.is_mass_deduplication && (
                    <span className={styles.dedupBadge}>🔀 Dedup</span>
                  )}
                  {!event.success && (
                    <span className={styles.errorBadge}>❌ Failed</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
